import React from 'react';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import ConfigurationViewContainer from 'js/components/elements/configViews/ConfigurationViewContainer';
import has from 'lodash/has';
import { useSelector } from 'react-redux';
import { DataSink } from 'new/entities/DataSink';
import { SwitchableSection } from 'new/elements/SwitchableSection/SwitchableSection';
import { Link } from 'react-router-dom';
import { ROUTE_MY_DATA_SETS } from 'js/constants/pathnames';
import { JSONView } from 'new/elements/JSONView/JSONView';
import { useDestinationOverview } from 'new/elements/Overview/Destination/useDestinationOverview';
import {
  formatDateRangeForApi,
  getPresetDateRange,
  MetricsDateRange,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { MetricsPanel } from '@modules/monitor/resource-triage/components/MetricsPanel';
import { useDestinationOverviewMetrics } from './useDestinationOverviewMetrics';
import { formatCode } from 'js/lib/FormatUtils';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import { CredentialWidget, useCredentialWidgetData } from 'new/elements/CredentialWidget';

import styles from '../Overview.module.scss';

interface DestinationOverviewProps {
  resource: DataSink;
}

export const DestinationOverview = (props: DestinationOverviewProps) => {
  const { resource } = props;
  const dataMaps = useSelector((s: any) => s.entities.dataMaps);

  const {
    credentials,
    lastUpdate,
    createdDate,
    writingDataSet,
    isWritingDataset,
    configurationsJSON,
    resourceTypeFields,
    isDataMap,
    connectionCategory,
  } = useDestinationOverview(resource);

  const {
    loading,
    metricsByDate,
    loadResourceMetricsAggregate,
    loadResourceMetricsPerDay,
    lifeMetrics,
  } = useDestinationOverviewMetrics(resource);

  const [metricsRange, setMetricsRange] = React.useState(() => getPresetDateRange('30d'));

  const onMetricsDatesChanged = (dates: MetricsDateRange) => {
    setMetricsRange({
      start: new Date(dates.start),
      end: new Date(dates.end),
    });
    loadResourceMetricsPerDay(formatDateRangeForApi(dates));
  };

  const onMetricsRefresh = () => {
    loadResourceMetricsAggregate();
    loadResourceMetricsPerDay(formatDateRangeForApi(metricsRange));
  };

  const dataMap =
    isDataMap && has(resource, 'data_map_id') && has(dataMaps, resource.data_map_id)
      ? dataMaps[resource.data_map_id]
      : null;

  const hasCredentials = Boolean(credentials);
  const credentialWidgetData = useCredentialWidgetData(credentials);
  let credentialsContent = credentialWidgetData ? (
    <CredentialWidget {...credentialWidgetData} />
  ) : null;

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(resource);

  const isDataMapElement = (
    <>
      {(isDataMap || hasCredentials) && (
        <div className="detail-key">{isDataMap ? 'Lookup ID ' : 'Credentials'}</div>
      )}

      {(isDataMap || hasCredentials) && (
        <div className="detail-value">{isDataMap ? resource.data_map : credentialsContent}</div>
      )}
    </>
  );

  let writingDataSetContent: {};
  if (isWritingDataset === true) {
    const writingDataSetName = writingDataSet ? writingDataSet.name : null;
    const _title = writingDataSetName ? 'Writing Nexset' : 'Writing Nexset ID';
    const _link = writingDataSetName ? writingDataSetName : resource.data_set_id;

    const linkTo = `${ROUTE_CONNECT}${ROUTE_MY_DATA_SETS}/${resource.data_set_id}`;
    writingDataSetContent = (
      <>
        <div className="detail-key">{_title}</div>
        <div className="detail-value">
          <Link to={linkTo} target="_blank" rel="noopener noreferrer">
            {_link}
          </Link>
        </div>
      </>
    );
  } else {
    writingDataSetContent = (
      <>
        <div className="detail-key">Writing Nexset ID</div>
        <div className="detail-value">Not configured to write a Nexset</div>
      </>
    );
  }

  const createdOnElement = (
    <>
      <div className="detail-key">Created On</div>
      <div className="detail-value">{createdDate}</div>
    </>
  );

  const lastUpdateOnElement = (
    <>
      <div className="detail-key">Last Updated on</div>
      <div className="detail-value">{lastUpdate}</div>
    </>
  );

  return (
    <div className={styles.sourceOverviewContainer}>
      <div className="tab-content">
        <section className="tab-content-section">
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Destination ID</div>
              <div className="detail-value">{resource.id}</div>
              {createdOnElement}
              {isDataMap ? isDataMapElement : null}
              {hasCredentials && !isDataMap ? (
                <>
                  <div className="detail-key">Credentials</div>
                  <div className="detail-value">{credentialsContent}</div>
                </>
              ) : null}
            </div>

            <div className="detail-col">
              {writingDataSetContent}
              {lastUpdateOnElement}
            </div>
          </div>
        </section>

        <hr />

        <section className="tab-content-section">
          <SwitchableSection
            title="Destination Configuration"
            switchTitle="JSON view"
            DefaultComponent={
              <ConfigurationViewContainer
                entityType={RESOURCE_TYPE_SINK}
                connectionCategory={connectionCategory}
                config={resource?.sink_config}
                configFields={resourceTypeFields}
                resourceId={resource.id}
                data={resource}
                dataMap={dataMap}
              />
            }
            SwitchableComponent={
              <div style={{ height: 300 }}>
                <JSONView json={formatCode(configurationsJSON, 'application/json')} />
              </div>
            }
          />
        </section>

        <hr />

        <section className="tab-content-section">
          <MetricsPanel
            lifetimeMetrics={lifeMetrics}
            trendData={metricsByDate}
            start={metricsRange.start}
            end={metricsRange.end}
            onDatesChanged={onMetricsDatesChanged}
            onRefresh={onMetricsRefresh}
            hideRecords={hideRecords}
            loading={loading}
            resourceType={RESOURCE_TYPE_SINK}
            resourceId={resource.id}
          />
        </section>
      </div>
    </div>
  );
};
